import { I18n } from "@assets/strings";
import { Set } from "immutable";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { ExerciseType } from "@domain/lms/course/exercise/exercise";
import { ExerciseDraft } from "@domain/lms/course/exercise/exerciseDraft";
import { useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { Card } from "@ui/components/containers/card";
import { ExerciseView } from "@ui/components/course/exercise/exerciseView";
import { ChevronDownIcon, DeleteIconBig, EditIcon } from "@ui/components/icons";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { gap } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { createToast } from "@ui/toasts/utils";
import { useChapterController } from "@ui/pages/lms/courseEditorPage/courseEditorController/editorControllerContext";
import { ExerciseEditor } from "./exerciseEditor";

interface ExerciseEditorWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
	exercise: ExerciseDraft & { type: ExerciseType };
	index: number;
}
export const ExerciseEditorWrapper: React.FC<ExerciseEditorWrapperProps> = ({ exercise, index, ...htmlProps }) => {
	const controller = useChapterController().exerciseController(exercise);
	const [collapsed, setCollapsed] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const initialExercise = useMemo(() => exercise, []);
	const initialIsEmpty = initialExercise.title === "";
	const isMobile = useIsMobile();

	return exercise.editing ? (
		<Wrapper {...htmlProps}>
			<ExerciseEditor index={index} exercise={exercise} controller={controller} />
			<Actions>
				<TertiaryButton
					onClick={() => {
						if (initialIsEmpty) {
							controller.removeExercise();
						} else {
							controller.setExerciseInfo(initialExercise);
							controller.toggleEditing();
						}
					}}
				>
					{I18n.t("cancel")}
				</TertiaryButton>
				<PrimaryButton
					onClick={() => {
						if (!exercise.title) {
							createToast(I18n.t("error"), I18n.t("course.editor.qcm.title_missing"), "error");
							return;
						}
						if (exercise.choices.find((c) => !c.text)) {
							createToast(I18n.t("error"), I18n.t("course.editor.qcm.answer_empty"), "error");
							return;
						}
						controller.toggleEditing();
					}}
				>
					{I18n.t("validate")}
				</PrimaryButton>
			</Actions>
		</Wrapper>
	) : (
		<RelativePreview
			index={index}
			exercise={exercise}
			currentAnswer={Set([exercise.choices.findIndex((c) => !!c.valid)])}
			preview
			collapsed={collapsed}
			onExtend={() => setCollapsed(false)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			{...htmlProps}
			renderMenu={() => (
				<StyledKebabMenu blur isVisible={isMobile ? true : isHovered}>
					<Row onClick={() => controller.toggleEditing()}>
						<EditIcon /> {I18n.t("edit")}
					</Row>
					<Row onClick={() => controller.removeExercise()}>
						<DeleteIconBig /> {I18n.t("delete")}
					</Row>
				</StyledKebabMenu>
			)}
			renderCollapser={() => (
				<Collapser collapsed={collapsed} onClick={() => setCollapsed(!collapsed)}>
					<ChevronDownIcon />
				</Collapser>
			)}
		/>
	);
};

const Wrapper = styled(Card)`
	padding: 32px 26px;
	border: none;
	${gap(20)};
`;
const RelativePreview = styled(ExerciseView)`
	position: relative;
`;

const StyledKebabMenu = styled(KebabMenu)`
	position: absolute;
	right: 22px;
	top: 22px;
`;

const Row = styled.div`
	cursor: pointer;
	display: flex;
	align-items: center;
	${({ theme }) => theme.textStyles.primary};
	&:not(:last-child) {
		margin-bottom: 18px;
	}

	svg {
		margin-right: 10px;
	}
`;

const Collapser = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "collapsed",
})<{ collapsed: boolean }>`
	cursor: pointer;
	position: absolute;
	right: -30px;
	top: 22px;
	z-index: ${Z.over};

	svg {
		flex-shrink: 0;
		${({ collapsed }) => !collapsed && "transform: rotate(180deg)"};
	}

	@media (max-width: 900px) {
		right: -20px;
		svg {
			width: 15px;
			height: 15px;
		}
	}
`;

const Actions = styled.div`
	display: flex;
	> :first-child {
		margin-right: 20px;
	}

	@media (max-width: 900px) {
		align-items: center;
		flex-direction: column-reverse;

		> :first-child {
			margin: 20px 0 0 0;
		}
	}
`;
